import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import DayPicker from '@acorn-portal/shared/src/components/datePicker/day-picker';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useFutureDateTime } from '@acorn-portal/shared/src/hooks/useFutureTimeSelection';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { CreatePrivateCarQuoteCommand, PrivateCarClient } from '@acorn-portal/shared/src/services/subscriptionAPI';
import { resetDrivers, setDriverData } from '@acorn-portal/shared/src/store/subscription-amend-drivers.slice';
import { setSubscriptionData } from '@acorn-portal/shared/src/store/subscription.slice';
import { resetSubscriptionChanges } from '@acorn-portal/shared/src/store/subscriptionChanges.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Schedule from '@mui/icons-material/Schedule';
import { Box, Collapse, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { add, formatISO, roundToNearestMinutes, set } from 'date-fns';
import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

const MIN = new Date();
const MAX = add(MIN, { days: 7 });

const SubscriptionRestart: FC = () => {
	const { policyToken } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const fullPolicy = useAppSelector((state) => state.fullPolicy);
	const [futureDatePicker, setFutureDatePicker] = useState(false);
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [loading, setLoading] = useState(false);

	const { hour, setHour, minute, setMinute, error, errorMessage, setMtaDateTime, setMtaStartDate } = useFutureDateTime(
		new Date(),
		futureDatePicker,
	);

	const handleDateChange = (date: Date | null | undefined) => {
		if (!date) return;
		setMtaDateTime(date);
		setMtaStartDate(date);
		setSelectedDate(date);
	};

	const ToggleFutureDatePicker = () => {
		setFutureDatePicker((prev) => !prev);
	};

	const handleContinue = async () => {
		if (!policyToken) return;

		setLoading(true);

		try {
			let effectiveDate: string;

			if (futureDatePicker && selectedDate !== null) {
				const temp = set(selectedDate, {
					hours: parseInt(hour),
					minutes: parseInt(minute),
				});
				effectiveDate = formatISO(temp);
			} else {
				const addTime = add(new Date(), { minutes: 15 });
				const nearest15Min = roundToNearestMinutes(addTime, {
					nearestTo: 15,
				});
				effectiveDate = formatISO(nearest15Min);
			}

			const url = import.meta.env.VITE_SUBSCRIPTION_API;
			const subscriptionClient = new PrivateCarClient(url, API);
			const body: CreatePrivateCarQuoteCommand = {
				policyToken: policyToken,
				startTime: effectiveDate,
			};

			const data = await subscriptionClient.createQuote(body);

			if (data.quoteResponse.underwrittenOptions?.[0]?.status === 'Declined') {
				setLoading(false);
				navigate(ROUTE_PATHS.error);
				return;
			}

			dispatch(
				setSubscriptionData({
					policyToken: policyToken,
					effectiveDate,
					sendWelcomePackStatus: undefined,
					autoRenew: false,
					quoteData: data,
				}),
			);

			// TODO: investigate bringing this into async thunk with the above once all journeys are complete (see PBI 49259)
			const driverDetails = data?.quoteDetails?.riskData?.driverDetails;
			if (driverDetails) {
				dispatch(setDriverData(driverDetails));
			} else {
				dispatch(resetDrivers());
			}

			dispatch(resetSubscriptionChanges());
			navigate(`${ROUTE_PATHS.subscriptionRestartSummary}/${policyToken}`);
		} catch {
			navigate(ROUTE_PATHS.error);
			setLoading(false);
		}
	};

	return (
		<JourneyCardLayout pageTitle='30-Day Subscription' loading={loading} loadingMessage='Generating your quote...'>
			<>
				<Box maxWidth='md' mx='auto' sx={{ p: 2 }}>
					<Box
						sx={{
							p: 2,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							width: '100%',
							mb: 6,
							mx: 'auto',
							border: '1px solid rgb(220, 163, 42)',
							borderRadius: '8px',
							backgroundColor: 'rgb(255, 253, 229)',
						}}
					>
						<Typography variant='body1' fontWeight='700'>
							Your 30 day subscription policy has expired
						</Typography>
						<Typography>
							You can restart your cover for another 30 days. If you choose to start now, your policy will begin 15
							minutes after you buy your policy. Alternatively, select a start date up to 7 days into the future and we
							will provide you with a new quote. You will see the exact start and end date/time on screen and in your
							documents after you buy your policy.
						</Typography>
					</Box>
					<Stack spacing={2}>
						<Box sx={{ textAlign: 'center' }}>
							<ToggleButtonGroup
								color='primary'
								value={futureDatePicker}
								exclusive
								onChange={ToggleFutureDatePicker}
								aria-label='Change Start Date Options'
							>
								<ToggleButton value={false}>
									<Schedule sx={{ pr: 1 }} />
									Start Now
								</ToggleButton>
								<ToggleButton value>
									<CalendarMonth sx={{ pr: 1 }} />
									Future Date
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>

						<Collapse in={futureDatePicker} data-testid='date-picker-collapse'>
							<DayPicker
								maxDate={MAX}
								minDate={MIN}
								time
								minute={minute}
								hour={hour}
								setHour={setHour}
								setMinute={setMinute}
								error={error}
								value={selectedDate ?? new Date()}
								errorMessage={errorMessage}
								onChange={handleDateChange}
							/>
						</Collapse>
					</Stack>
				</Box>

				<ActionButtons>
					<BackButton
						onClick={() => {
							const encodedPolicyNumber = encodeURIComponent(fullPolicy.policyNumber);
							navigate(`${ROUTE_PATHS.policyDetails}/${policyToken}?policyNumber=${encodedPolicyNumber}`);
						}}
					>
						Policy Details
					</BackButton>
					<ContinueButton disabled={error || loading} onClick={handleContinue}>
						Generate Quote
					</ContinueButton>
				</ActionButtons>
			</>
		</JourneyCardLayout>
	);
};

export default SubscriptionRestart;
